import { Button, Card, Select, StatusMessage, TextInput } from "@herefor/design-system";
import {
  instant,
  localDate,
  localDateOf,
  startOfLocalWeek,
  timeZone,
} from "@herefor/domain-kernel";
import { OFFLINE_MESSAGES as COPY } from "@herefor/product-language";
import { useConvexConnectionState, useMutation } from "convex/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react";

import { availabilityMutations } from "../convex/availability";

export type ConnectivityPhase = "online" | "offline" | "refreshing";

interface ConnectivityValue {
  readonly phase: ConnectivityPhase;
  readonly actionsPaused: boolean;
  readonly refreshCycle: number;
  readonly confirmReadsFresh: (cycle: number) => void;
}

const ONLINE_DEFAULT: ConnectivityValue = {
  phase: "online",
  actionsPaused: false,
  refreshCycle: 0,
  confirmReadsFresh: () => undefined,
};

const ConnectivityContext = createContext<ConnectivityValue>(ONLINE_DEFAULT);

/**
 * Global fail-closed connection boundary.
 *
 * A websocket reconnect is not enough to re-enable actions. The live plan route
 * confirms a fresh server read for the current `refreshCycle`; until then the phase
 * remains `refreshing`. That ordering prevents a person acting on a cached plan in
 * the small gap between transport recovery and query refresh.
 */
export function ConnectivityProvider({ children }: { readonly children: ReactNode }) {
  const connection = useConvexConnectionState();
  const [browserOnline, setBrowserOnline] = useState(readBrowserOnline);
  const [offlineCycle, setOfflineCycle] = useState(0);
  const [freshCycle, setFreshCycle] = useState<number | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setBrowserOnline(true);
    };
    const handleOffline = () => {
      setBrowserOnline(false);
      setOfflineCycle((current) => current + 1);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const transportReady = browserOnline && connection.isWebSocketConnected;
  // `connectionCount` advances on a websocket reconnect; `offlineCycle` advances
  // immediately on the browser's offline event, before the socket necessarily has
  // time to notice. Either kind of outage therefore creates a new refresh barrier.
  const refreshCycle = connection.connectionCount + offlineCycle;

  const confirmReadsFresh = useCallback(
    (cycle: number) => {
      if (transportReady && cycle === refreshCycle) setFreshCycle(cycle);
    },
    [refreshCycle, transportReady],
  );

  const phase = connectivityPhase({
    browserOnline,
    socketConnected: connection.isWebSocketConnected,
    refreshCycle,
    freshCycle,
  });

  const value = useMemo<ConnectivityValue>(
    () => ({
      phase,
      actionsPaused: phase !== "online",
      refreshCycle,
      confirmReadsFresh,
    }),
    [confirmReadsFresh, phase, refreshCycle],
  );

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity(): ConnectivityValue {
  return useContext(ConnectivityContext);
}

export function connectivityPhase(input: {
  readonly browserOnline: boolean;
  readonly socketConnected: boolean;
  readonly refreshCycle: number;
  readonly freshCycle: number | null;
}): ConnectivityPhase {
  if (!input.browserOnline) return "offline";
  if (!input.socketConnected || input.freshCycle !== input.refreshCycle) return "refreshing";
  return "online";
}

const FRESH_READ_CONFIRMATION_DELAY_MS = 250;
const FRESH_READ_RETRY_INITIAL_MS = 500;
const FRESH_READ_RETRY_MAX_MS = 5_000;

/**
 * Keeps the reconnect barrier closed until a live read succeeds, while recovering
 * from transient transport or identity-attachment failures without a page reload.
 */
export function startFreshReadConfirmation(
  read: () => Promise<unknown>,
  confirm: () => void,
): () => void {
  let active = true;
  let failureCount = 0;
  let timer: number | undefined;

  const attempt = () => {
    void read().then(
      () => {
        if (!active) return;
        // Keep the refresh state visible for one calm beat. Besides making the
        // transition honest, this guarantees a committed render in which every
        // consequential control is disabled before the barrier opens.
        timer = window.setTimeout(() => {
          if (active) confirm();
        }, FRESH_READ_CONFIRMATION_DELAY_MS);
      },
      () => {
        if (!active) return;
        const retryDelay = Math.min(
          FRESH_READ_RETRY_INITIAL_MS * 2 ** failureCount,
          FRESH_READ_RETRY_MAX_MS,
        );
        failureCount += 1;
        timer = window.setTimeout(attempt, retryDelay);
      },
    );
  };

  attempt();
  return () => {
    active = false;
    if (timer !== undefined) window.clearTimeout(timer);
  };
}

export function ConnectivityBanner() {
  const { phase } = useConnectivity();
  if (phase === "online") return null;

  return (
    <div className="offline-banner" data-connectivity={phase}>
      <StatusMessage tone={phase === "offline" ? "warning" : "info"} live="polite">
        {phase === "offline" ? (
          <>
            <strong>{COPY.offline}</strong> {COPY.staleReads}
          </>
        ) : (
          COPY.refreshing
        )}
      </StatusMessage>
    </div>
  );
}

type AvailabilityBlock = "morning" | "afternoon" | "evening";
type AvailabilityHelpMode = "in_person" | "driving" | "phone_or_online";

interface AvailabilityDraft {
  readonly version: 1;
  readonly dateLocal: string;
  readonly blockId: AvailabilityBlock;
  readonly helpMode: AvailabilityHelpMode;
  readonly draftKey: string;
}

const BLOCK_OPTIONS = [
  { value: "morning", label: COPY.availabilityDraft.blocks.morning },
  { value: "afternoon", label: COPY.availabilityDraft.blocks.afternoon },
  { value: "evening", label: COPY.availabilityDraft.blocks.evening },
] as const;

const HELP_MODE_OPTIONS = [
  { value: "in_person", label: COPY.availabilityDraft.helpModes.inPerson },
  { value: "driving", label: COPY.availabilityDraft.helpModes.driving },
  { value: "phone_or_online", label: COPY.availabilityDraft.helpModes.phoneOrOnline },
] as const;

/** PLT-011's sole offline-write exception: a local, visibly unsent availability draft. */
export function AvailabilityDraftPanel({
  circleId,
  hideWhenOnlineWithoutDraft = false,
  timeZone: zoneName,
  viewerPersonId,
}: {
  readonly circleId: string;
  readonly hideWhenOnlineWithoutDraft?: boolean;
  readonly timeZone: string;
  /** Server-resolved owner of this self-availability draft. */
  readonly viewerPersonId: string;
}) {
  const zone = timeZone(zoneName);
  const { actionsPaused, phase } = useConnectivity();
  const recordSelf = useMutation(availabilityMutations.recordSelf);
  const storageKey = useMemo(
    () => draftStorageKey(circleId, viewerPersonId),
    [circleId, viewerPersonId],
  );
  const initialDraft = useMemo(() => readDraft(storageKey), [storageKey]);
  const [dateLocal, setDateLocal] = useState(
    () => initialDraft?.dateLocal ?? String(localDateOf(instant(Date.now()), zone)),
  );
  const [blockId, setBlockId] = useState<AvailabilityBlock>(
    () => initialDraft?.blockId ?? "morning",
  );
  const [helpMode, setHelpMode] = useState<AvailabilityHelpMode>(
    () => initialDraft?.helpMode ?? "in_person",
  );
  const [draft, setDraft] = useState<AvailabilityDraft | null>(initialDraft);
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"saved" | "sent" | null>(null);
  const [sending, setSending] = useState(false);

  if (hideWhenOnlineWithoutDraft && phase !== "offline" && draft === null && outcome !== "sent")
    return null;

  const saveDraft = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    if (!isLocalDate(dateLocal)) {
      setProblem(COPY.availabilityDraft.invalidDate);
      return;
    }

    const saved: AvailabilityDraft = {
      version: 1,
      dateLocal,
      blockId,
      helpMode,
      draftKey: draft?.draftKey ?? `availability-draft-${crypto.randomUUID()}`,
    };
    if (!writeDraft(storageKey, saved)) {
      setProblem(COPY.availabilityDraft.storageUnavailable);
      return;
    }
    setDraft(saved);
    setProblem(null);
    setOutcome("saved");
  };

  const sendDraft = () => {
    if (draft === null || actionsPaused || sending) return;

    setSending(true);
    setProblem(null);
    setOutcome(null);
    void recordSelf({
      circleId,
      weekStartLocal: String(startOfLocalWeek(localDate(draft.dateLocal))),
      entries: [
        {
          kind: "block",
          dateLocal: draft.dateLocal,
          blockId: draft.blockId,
          helpModes: [draft.helpMode],
        },
      ],
      idempotencyKey: draft.draftKey,
    })
      .then(() => {
        removeDraft(storageKey);
        setDraft(null);
        setOutcome("sent");
      })
      .catch(() => {
        setProblem(COPY.availabilityDraft.sendFailed);
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <section aria-labelledby="availability-draft-title" data-availability-draft>
      <Card>
        <h2 id="availability-draft-title">{COPY.availabilityDraft.title}</h2>
        <p>{COPY.availabilityDraft.introduction}</p>
        <form onSubmit={saveDraft}>
          <TextInput
            label={COPY.availabilityDraft.dateLabel}
            type="date"
            required
            value={dateLocal}
            onChange={(event) => {
              setDateLocal(event.currentTarget.value);
            }}
          />
          <Select
            label={COPY.availabilityDraft.blockLabel}
            options={BLOCK_OPTIONS}
            value={blockId}
            onChange={(event) => {
              setBlockId(asBlock(event.currentTarget.value));
            }}
          />
          <Select
            label={COPY.availabilityDraft.helpModeLabel}
            options={HELP_MODE_OPTIONS}
            value={helpMode}
            onChange={(event) => {
              setHelpMode(asHelpMode(event.currentTarget.value));
            }}
          />
          <Button type="submit" variant="secondary">
            {COPY.availabilityDraft.save}
          </Button>
        </form>

        {draft === null ? null : (
          <div data-draft-state="unsent">
            <StatusMessage tone="warning" live="polite">
              <strong>{COPY.availabilityDraft.notSent}</strong> {COPY.availabilityDraft.saved}
            </StatusMessage>
            <Button disabled={actionsPaused || sending} onClick={sendDraft} variant="primary">
              {COPY.availabilityDraft.send}
            </Button>
          </div>
        )}
        {outcome === "sent" ? (
          <StatusMessage tone="success" live="polite">
            {COPY.availabilityDraft.sent}
          </StatusMessage>
        ) : null}
        {problem === null ? null : (
          <StatusMessage tone="error" live="assertive">
            {problem}
          </StatusMessage>
        )}
      </Card>
    </section>
  );
}

function readBrowserOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

function draftStorageKey(circleId: string, viewerPersonId: string): string {
  // A browser may be shared by multiple circle members. `recordSelf` attributes a
  // send to the CURRENT actor, so a circle-only key would let the next signed-in
  // person publish somebody else's unsent draft as their own. Both identifiers come
  // from the authorised live-plan read; neither is inferred in the browser.
  return `herefor:availability-draft:v2:${circleId}:${viewerPersonId}`;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readDraft(key: string): AvailabilityDraft | null {
  try {
    const serialized = safeStorage()?.getItem(key);
    if (serialized === undefined || serialized === null) return null;
    const candidate: unknown = JSON.parse(serialized);
    return isAvailabilityDraft(candidate) ? candidate : null;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: AvailabilityDraft): boolean {
  try {
    const storage = safeStorage();
    if (storage === null) return false;
    storage.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

function removeDraft(key: string): void {
  try {
    safeStorage()?.removeItem(key);
  } catch {
    // The server write succeeded. A stale local draft is harmless because its stable
    // idempotency key can only replay that same write if storage later becomes usable.
  }
}

function isAvailabilityDraft(value: unknown): value is AvailabilityDraft {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    item["version"] === 1 &&
    typeof item["dateLocal"] === "string" &&
    isLocalDate(item["dateLocal"]) &&
    isBlock(item["blockId"]) &&
    isHelpMode(item["helpMode"]) &&
    typeof item["draftKey"] === "string" &&
    item["draftKey"].length <= 128
  );
}

function isLocalDate(value: string): boolean {
  try {
    localDate(value);
    return true;
  } catch {
    return false;
  }
}

function isBlock(value: unknown): value is AvailabilityBlock {
  return value === "morning" || value === "afternoon" || value === "evening";
}

function asBlock(value: string): AvailabilityBlock {
  return isBlock(value) ? value : "morning";
}

function isHelpMode(value: unknown): value is AvailabilityHelpMode {
  return value === "in_person" || value === "driving" || value === "phone_or_online";
}

function asHelpMode(value: string): AvailabilityHelpMode {
  return isHelpMode(value) ? value : "in_person";
}

export const offlineStylesheet = `
  .offline-banner {
    position: sticky;
    z-index: 20;
    top: 0;
    padding: var(--herefor-space-2) var(--herefor-space-4);
    background: var(--herefor-color-canvas);
    border-bottom: 1px solid var(--herefor-color-border);
  }

  [data-availability-draft] {
    margin-block-end: var(--herefor-space-6);
  }

  [data-availability-draft] form,
  [data-draft-state="unsent"] {
    display: grid;
    gap: var(--herefor-space-3);
  }
`;
